import { useState } from 'react'
import { toast } from 'sonner'
import { Input } from '@/components/ui/shadcn/input'
import { Button } from '@/components/ui/shadcn/button'

interface Props {
  /** Current "W:H" aspect ratio, used to prefill the fields. */
  currentAspect: string
  onApply: (aspect: string) => void
}

const MAX_SIDE = 9999
/** Cap the longest/shortest-side ratio so a canvas can't blow up to absurd sizes. */
const MAX_RATIO = 10

const splitAspect = (aspect: string): [string, string] => {
  const [w, h] = aspect.split(':')
  return [w ?? '', h ?? '']
}

export function AspectRatioCustomInput({ currentAspect, onApply }: Props) {
  const [width, setWidth] = useState(() => splitAspect(currentAspect)[0])
  const [height, setHeight] = useState(() => splitAspect(currentAspect)[1])

  // Reset the fields when the active aspect ratio changes (e.g. a preset was
  // picked) — done during render rather than in an effect, per React guidance.
  const [prevAspect, setPrevAspect] = useState(currentAspect)
  if (currentAspect !== prevAspect) {
    setPrevAspect(currentAspect)
    const [w, h] = splitAspect(currentAspect)
    setWidth(w)
    setHeight(h)
  }

  const apply = () => {
    const w = Number(width)
    const h = Number(height)
    if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) {
      toast.error('Enter whole numbers for width and height.')
      return
    }
    if (w > MAX_SIDE || h > MAX_SIDE) {
      toast.error(`Width and height must be ${MAX_SIDE} or less.`)
      return
    }
    if (Math.max(w, h) / Math.min(w, h) > MAX_RATIO) {
      toast.error('That aspect ratio is too extreme.')
      return
    }
    onApply(`${w}:${h}`)
  }

  return (
    <div
      className="border-border mt-1 flex flex-col gap-2 border-t px-1 pt-2"
      // Stop the dropdown's typeahead from swallowing keystrokes typed into the
      // inputs; Enter applies.
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Enter') apply()
      }}>
      <p className="font-regular text-muted-foreground text-sm">Custom</p>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          min={1}
          value={width}
          onChange={(e) => setWidth(e.target.value)}
          placeholder="W"
          aria-label="Custom aspect ratio width"
          className="h-8 w-16"
        />
        <span className="text-muted-foreground text-sm">:</span>
        <Input
          type="number"
          min={1}
          value={height}
          onChange={(e) => setHeight(e.target.value)}
          placeholder="H"
          aria-label="Custom aspect ratio height"
          className="h-8 w-16"
        />
        <Button variant="secondary" size="sm" className="h-8" onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  )
}
